import { devLog, devError } from '../utils/devLogger';
import { db } from '../firebase';
import {
  collection,
  addDoc,
  updateDoc,
  getDocs,
  doc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';

class AIMeetingRecapService {
  constructor() {
    // API calls are proxied through a Cloud Function — no key in the browser bundle
    this.proxyEndpoint = '/api/generateAiRecap';
    this.model = 'gpt-3.5-turbo';
    // Legacy direct-key path kept for local dev only; ignored in production
    this.apiKey = process.env.REACT_APP_OPENAI_API_KEY || '';
  }

  /**
   * Generate AI recap from meeting transcript
   * @param {string} transcript - The meeting transcript
   * @param {Object} meetingData - Meeting metadata
   * @returns {Object} Recap with key points, action items, and summary
   */
  async generateRecap(transcript, meetingData = {}) {
    try {
      const prompt = this.createRecapPrompt(transcript, meetingData);

      // Call through the server-side proxy — no key in the browser bundle
      const startTime = Date.now();
      const aiResponse = await this.callAIAPI(prompt, transcript, meetingData);
      const processingTime = ((Date.now() - startTime) / 1000).toFixed(1);

      if (aiResponse === null) {
        // Proxy returned 503 — service not configured, use simulation
        const mockResponse = await this.simulateAIResponse(transcript, meetingData);
        return { ...mockResponse, source: 'simulated' };
      }

      return {
        success: true,
        recap: this.parseAIResponse(aiResponse, processingTime),
        source: 'openai',
        model: this.model
      };
    } catch (error) {
      devError('Error generating AI recap:', error);
      // If AI fails, fall back to simulation
      devLog('AI generation failed. Falling back to simulated response.');
      const mockResponse = await this.simulateAIResponse(transcript, meetingData);
      return { ...mockResponse, source: 'fallback', error: error.message };
    }
  }

  /**
   * Call OpenAI via the server-side proxy. Returns null when the service is not
   * configured (503), allowing graceful fallback to simulation.
   */
  async callAIAPI(_prompt, transcript, meetingData) {
    try {
      const response = await fetch(this.proxyEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript, meetingData, model: this.model })
      });

      if (response.status === 503) {
        return null; // service not configured — caller falls back to simulation
      }

      if (!response.ok) {
        throw new Error(`AI proxy error: ${response.status}`);
      }

      const data = await response.json();
      return data.content || '';
    } catch (error) {
      devError('AI proxy call failed:', error);
      throw error;
    }
  }

  /**
   * Parse AI response into structured format
   */
  parseAIResponse(aiText, processingTime) {
    const sections = {
      executiveSummary: '',
      keyLearningPoints: [],
      actionItems: [],
      homework: [],
      resourcesMentioned: [],
      nextSessionPreview: '',
      attendanceNotes: '',
      generatedAt: new Date().toISOString(),
      confidence: 90,
      processingTime: `${processingTime} seconds`
    };

    try {
      // Extract sections using regex patterns
      const summaryMatch = aiText.match(/\*\*EXECUTIVE SUMMARY\*\*([\s\S]*?)(?=\*\*|$)/i);
      if (summaryMatch) {
        sections.executiveSummary = summaryMatch[1].trim();
      }

      const keyPointsMatch = aiText.match(/\*\*KEY LEARNING POINTS\*\*([\s\S]*?)(?=\*\*|$)/i);
      if (keyPointsMatch) {
        sections.keyLearningPoints = keyPointsMatch[1]
          .split('\n')
          .filter(line => line.trim().match(/^[\d\-\*•]/))
          .map(line => line.replace(/^[\d\-\*•]\s*/, '').trim())
          .filter(line => line.length > 0);
      }

      const actionItemsMatch = aiText.match(/\*\*ACTION ITEMS\*\*([\s\S]*?)(?=\*\*|$)/i);
      if (actionItemsMatch) {
        sections.actionItems = actionItemsMatch[1]
          .split('\n')
          .filter(line => line.trim().match(/^[\d\-\*•]/))
          .map(line => line.replace(/^[\d\-\*•]\s*/, '').trim())
          .filter(line => line.length > 0);
      }

      const homeworkMatch = aiText.match(/\*\*HOMEWORK\/FOLLOW-UP\*\*([\s\S]*?)(?=\*\*|$)/i);
      if (homeworkMatch) {
        sections.homework = homeworkMatch[1]
          .split('\n')
          .filter(line => line.trim().match(/^[\d\-\*•]/))
          .map(line => line.replace(/^[\d\-\*•]\s*/, '').trim())
          .filter(line => line.length > 0);
      }

      const resourcesMatch = aiText.match(/\*\*RESOURCES MENTIONED\*\*([\s\S]*?)(?=\*\*|$)/i);
      if (resourcesMatch) {
        sections.resourcesMentioned = resourcesMatch[1]
          .split('\n')
          .filter(line => line.trim().match(/^[\d\-\*•]/))
          .map(line => line.replace(/^[\d\-\*•]\s*/, '').trim())
          .filter(line => line.length > 0);
      }

      const nextSessionMatch = aiText.match(/\*\*NEXT SESSION PREVIEW\*\*([\s\S]*?)(?=\*\*|$)/i);
      if (nextSessionMatch) {
        sections.nextSessionPreview = nextSessionMatch[1].trim();
      }

      const attendanceMatch = aiText.match(/\*\*ATTENDANCE NOTES\*\*([\s\S]*?)(?=\*\*|$)/i);
      if (attendanceMatch) {
        sections.attendanceNotes = attendanceMatch[1].trim();
      }

      // Fallback to simpler parsing if structured format not found
      if (!sections.executiveSummary && aiText.length > 50) {
        const firstParagraph = aiText.split('\n\n')[0];
        sections.executiveSummary = firstParagraph.substring(0, 500);
      }

      return sections;
    } catch (error) {
      devError('Error parsing AI response:', error);
      // Return minimal structure if parsing fails
      return {
        ...sections,
        executiveSummary: aiText.substring(0, 500),
        confidence: 50
      };
    }
  }

  /**
   * Create structured prompt for AI recap generation
   */
  createRecapPrompt(transcript, meetingData) {
    return `
Please analyze this learning session transcript and create a comprehensive recap:

SESSION DETAILS:
- Title: ${meetingData.title || 'Learning Session'}
- Date: ${meetingData.date || new Date().toLocaleDateString()}
- Duration: ${meetingData.duration || 'Unknown'}
- Facilitator: ${meetingData.facilitator || 'Unknown'}
- Module: ${meetingData.module || 'General'}

TRANSCRIPT:
${transcript}

Please provide a structured recap in the following format:

**EXECUTIVE SUMMARY** (2-3 sentences)

**KEY LEARNING POINTS** (numbered list of main concepts covered)

**ACTION ITEMS** (specific tasks for learners)

**HOMEWORK/FOLLOW-UP** (what learners should do before next session)

**RESOURCES MENTIONED** (any materials, links, or tools discussed)

**NEXT SESSION PREVIEW** (if mentioned)

**ATTENDANCE NOTES** (participation levels, engagement observations)

Focus on educational value and make it useful for learners who want to review or missed the session.
`;
  }

  /**
   * Simulate AI response for demo purposes
   */
  async simulateAIResponse(transcript, meetingData) {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    const wordCount = transcript.split(' ').length;
    const hasKeywords = {
      bee: transcript.toLowerCase().includes('bee') || transcript.toLowerCase().includes('empowerment'),
      digital: transcript.toLowerCase().includes('digital') || transcript.toLowerCase().includes('technology'),
      assessment: transcript.toLowerCase().includes('assessment') || transcript.toLowerCase().includes('quiz'),
      project: transcript.toLowerCase().includes('project') || transcript.toLowerCase().includes('assignment')
    };

    return {
      success: true,
      recap: {
        executiveSummary: this.generateExecutiveSummary(meetingData, hasKeywords, wordCount),
        keyLearningPoints: this.generateKeyPoints(hasKeywords),
        actionItems: this.generateActionItems(hasKeywords),
        homework: this.generateHomework(hasKeywords),
        resourcesMentioned: this.generateResources(hasKeywords),
        nextSessionPreview: this.generateNextSession(hasKeywords),
        attendanceNotes: this.generateAttendanceNotes(wordCount),
        generatedAt: new Date().toISOString(),
        confidence: Math.min(95, Math.max(75, wordCount / 10)), // Higher confidence with more content
        processingTime: '2.3 seconds'
      }
    };
  }

  generateExecutiveSummary(meetingData, keywords, wordCount) {
    if (keywords.bee) {
      return `This session covered essential concepts in Broad-Based Black Economic Empowerment (BEE), focusing on practical applications for learners. The discussion included compliance requirements, scoring mechanisms, and implementation strategies. Learners demonstrated good engagement with ${wordCount > 500 ? 'comprehensive' : 'focused'} discussion on real-world applications.`;
    }
    if (keywords.digital) {
      return `The session explored digital literacy and technology skills essential for modern workplace success. Participants learned about digital tools, online safety, and effective use of technology for productivity. The interactive format encouraged hands-on practice and collaborative learning.`;
    }
    if (keywords.assessment) {
      return `This session focused on assessment methods and evaluation techniques. Learners reviewed quiz formats, grading criteria, and effective study strategies. The discussion emphasized both formative and summative assessment approaches.`;
    }
    return `This learning session covered ${meetingData.module || 'core curriculum'} topics with active learner participation. The facilitator presented key concepts while encouraging discussion and practical application of the material.`;
  }

  generateKeyPoints(keywords) {
    const basePoints = [
      'Introduction to session objectives and learning outcomes',
      'Review of previous session materials and assignments'
    ];

    if (keywords.bee) {
      return [
        ...basePoints,
        'BEE scorecard components and calculation methods',
        'Procurement policies and preferential scoring',
        'Skills development requirements and compliance',
        'Enterprise and supplier development strategies',
        'Socio-economic development initiatives',
        'Verification and certification processes'
      ];
    }
    if (keywords.digital) {
      return [
        ...basePoints,
        'Essential digital literacy skills for workplace success',
        'Email etiquette and professional communication',
        'File management and cloud storage solutions',
        'Online research techniques and source verification',
        'Digital security and privacy best practices',
        'Collaborative tools and virtual meeting platforms'
      ];
    }
    if (keywords.assessment) {
      return [
        ...basePoints,
        'Different types of assessment methods and their purposes',
        'Creating effective study schedules and review strategies',
        'Understanding grading rubrics and evaluation criteria',
        'Self-assessment techniques and reflection practices',
        'Preparing for various question formats',
        'Feedback interpretation and improvement planning'
      ];
    }
    return [
      ...basePoints,
      'Core concepts and theoretical frameworks',
      'Practical applications and real-world examples',
      'Interactive exercises and group discussions',
      'Q&A session addressing learner concerns'
    ];
  }

  generateActionItems(keywords) {
    const baseItems = [
      'Review session notes and supplementary materials',
      'Complete assigned readings before next session'
    ];

    if (keywords.bee) {
      return [
        ...baseItems,
        'Research BEE requirements for your industry sector',
        'Complete BEE scorecard calculation exercise',
        'Identify potential supplier development opportunities',
        'Prepare case study analysis for next session'
      ];
    }
    if (keywords.digital) {
      return [
        ...baseItems,
        'Practice using the demonstrated digital tools',
        'Set up cloud storage account and organize files',
        'Complete online safety checklist',
        'Join the class collaboration platform'
      ];
    }
    if (keywords.project) {
      return [
        ...baseItems,
        'Begin work on assigned project deliverables',
        'Form project teams and establish communication channels',
        'Conduct initial research and create project timeline',
        'Schedule team check-in meetings'
      ];
    }
    return [
      ...baseItems,
      'Practice the techniques discussed in today\'s session',
      'Prepare questions for next week\'s Q&A segment',
      'Complete the assigned reflection exercise'
    ];
  }

  generateHomework(keywords) {
    if (keywords.bee) {
      return [
        'Read Chapter 3: "BEE Implementation Strategies" in the course textbook',
        'Complete the online BEE knowledge assessment (due in 3 days)',
        'Research and document one successful BEE case study',
        'Prepare a 5-minute presentation on sector-specific BEE requirements'
      ];
    }
    if (keywords.digital) {
      return [
        'Complete the Digital Skills Practice Module on the LMS',
        'Set up your professional email signature',
        'Practice creating and sharing documents using cloud platforms',
        'Watch the supplementary video on "Advanced Excel Functions"'
      ];
    }
    return [
      'Review all materials covered in today\'s session',
      'Complete the assigned practice exercises',
      'Prepare for next session\'s discussion topics',
      'Submit reflection journal entry by Friday'
    ];
  }

  generateResources(keywords) {
    const baseResources = [
      'Session slides (available on LMS)',
      'Course textbook: Chapters covered today'
    ];

    if (keywords.bee) {
      return [
        ...baseResources,
        'BEE Commission official website: www.beecommission.co.za',
        'DTI Codes of Good Practice documentation',
        'BEE scorecard calculation template (Excel)',
        'Verification agencies contact directory',
        '"BEE Made Simple" resource guide (PDF)'
      ];
    }
    if (keywords.digital) {
      return [
        ...baseResources,
        'Microsoft Office 365 training portal',
        'Google Workspace learning center',
        'Cybersecurity awareness course (Coursera link)',
        'Digital literacy assessment tool',
        'Recommended apps list for productivity'
      ];
    }
    return [
      ...baseResources,
      'Supplementary reading materials (LMS library)',
      'Practice exercises and answer keys',
      'External learning resources and websites',
      'Recommended videos for further learning'
    ];
  }

  generateNextSession(keywords) {
    if (keywords.bee) {
      return 'Next week: "BEE Verification Process" - We\'ll explore the verification audit process, preparing for verification, and maintaining compliance. Bring your completed scorecard exercises.';
    }
    if (keywords.digital) {
      return 'Next session: "Advanced Digital Tools" - We\'ll dive deeper into productivity applications, automation tools, and digital project management. Come prepared to work hands-on.';
    }
    if (keywords.assessment) {
      return 'Next week: "Assessment Design Workshop" - Learners will practice creating their own assessment questions and receive peer feedback. Bring examples of good questions you\'ve encountered.';
    }
    return 'Next session will build on today\'s concepts with practical applications and advanced topics. Review materials will be provided beforehand.';
  }

  generateAttendanceNotes(wordCount) {
    if (wordCount > 800) {
      return 'Excellent participation with active Q&A throughout the session. Multiple learners contributed examples and asked clarifying questions. High engagement level observed.';
    }
    if (wordCount > 400) {
      return 'Good attendance with moderate participation. Some learners actively engaged while others listened attentively. Overall positive learning environment.';
    }
    return 'Standard session with basic participation. Facilitator-led with some learner interaction. Consider strategies to increase engagement for future sessions.';
  }

  /**
   * Save meeting recap to database
   */
  async saveRecap(recap, meetingId, facilitatorId) {
    try {
      const recapData = {
        meetingId,
        facilitatorId,
        ...recap,
        createdAt: serverTimestamp(),
        isPublished: false,
        learnerFeedback: [],
        viewCount: 0
      };

      const docRef = await addDoc(collection(db, 'meetingRecaps'), recapData);
      return { success: true, id: docRef.id };
    } catch (error) {
      devError('Error saving recap:', error);
      throw new Error('Failed to save recap: ' + error.message);
    }
  }

  /**
   * Get recaps for a specific facilitator
   */
  async getFacilitatorRecaps(facilitatorId) {
    try {
      const q = query(
        collection(db, 'meetingRecaps'),
        where('facilitatorId', '==', facilitatorId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));
    } catch (error) {
      devError('Error getting recaps:', error);
      throw new Error('Failed to load recaps: ' + error.message);
    }
  }

  /**
   * Publish recap to learners
   */
  async publishRecap(recapId, notifyLearners = true) {
    try {
      await updateDoc(doc(db, 'meetingRecaps', recapId), {
        isPublished: true,
        publishedAt: serverTimestamp()
      });

      if (notifyLearners) {
        // Trigger notification to learners
        await this.notifyLearnersOfRecap(recapId);
      }

      return { success: true };
    } catch (error) {
      devError('Error publishing recap:', error);
      throw new Error('Failed to publish recap: ' + error.message);
    }
  }

  /**
   * Send notifications to learners about new recap
   */
  async notifyLearnersOfRecap(recapId) {
    try {
      // Create notification record
      await addDoc(collection(db, 'notifications'), {
        type: 'meeting_recap_available',
        recapId,
        title: 'New Meeting Recap Available',
        message: 'A recap from your recent learning session is now available to view.',
        createdAt: serverTimestamp(),
        isRead: false
      });

      return { success: true };
    } catch (error) {
      devError('Error notifying learners:', error);
      // Don't throw error - notification failure shouldn't break recap publishing
      return { success: false, error: error.message };
    }
  }

  /**
   * Get available recaps for learners
   */
  async getLearnerRecaps(learnerId, moduleId = null) {
    try {
      let q = query(
        collection(db, 'meetingRecaps'),
        where('isPublished', '==', true),
        orderBy('createdAt', 'desc')
      );

      if (moduleId) {
        q = query(q, where('moduleId', '==', moduleId));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        publishedAt: doc.data().publishedAt?.toDate()
      }));
    } catch (error) {
      devError('Error getting learner recaps:', error);
      throw new Error('Failed to load recaps: ' + error.message);
    }
  }

  /**
   * Track recap view
   */
  async trackRecapView(recapId, userId) {
    try {
      const recapRef = doc(db, 'meetingRecaps', recapId);

      // Increment view count
      await updateDoc(recapRef, {
        viewCount: serverTimestamp() // Use increment in production
      });

      // Track individual user view
      await addDoc(collection(db, 'recapViews'), {
        recapId,
        userId,
        viewedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      devError('Error tracking view:', error);
      // Non-critical error, don't throw
      return { success: false };
    }
  }

  /**
   * Validate transcript quality
   */
  validateTranscript(transcript) {
    if (!transcript || typeof transcript !== 'string') {
      return { valid: false, error: 'No transcript provided' };
    }

    const wordCount = transcript.split(' ').length;
    if (wordCount < 50) {
      return { valid: false, error: 'Transcript too short (minimum 50 words)' };
    }

    if (wordCount > 10000) {
      return { valid: false, error: 'Transcript too long (maximum 10,000 words)' };
    }

    // Check for common transcript artifacts
    const hasTimestamps = /\d{1,2}:\d{2}/.test(transcript);
    const hasSpeakerLabels = /Speaker \d|[A-Z][a-z]+:/.test(transcript);

    return {
      valid: true,
      wordCount,
      hasTimestamps,
      hasSpeakerLabels,
      quality: wordCount > 500 ? 'high' : wordCount > 200 ? 'medium' : 'low'
    };
  }
}

export default new AIMeetingRecapService();